import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { Download, ExternalLink, X } from 'lucide-react';

interface LightboxState { src: string; name: string }

const FOCUSABLE =
  'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

/** Lightbox voor artikelafbeeldingen: elke <img> in de prose en elk element
 *  met [data-lightbox] opent hem — met muis én toetsenbord. Esc en de
 *  achtergrond sluiten, de body scrollt niet mee, Tab blijft binnen de
 *  dialoog, en na sluiten keert de focus terug naar waar je vandaan kwam. */
export function ImageLightbox({ scopeRef, downloadBase }: {
  scopeRef: RefObject<HTMLElement | null>;
  downloadBase: string; // bv. /artikel/<slug>
}) {
  const [img, setImg] = useState<LightboxState | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<HTMLElement | null>(null);

  /* Een ARTIKELKAART is geen schermafbeelding.
     De tegel op zo'n kaart staat in een <a> die naar een ander kennisitem
     wijst; hem vergroten is nooit de bedoeling, en de lightbox zou de klik
     bovendien van de link afsnoepen (openFrom doet preventDefault). Ook de
     markering hieronder moet hem overslaan: anders krijgt een plaatje binnen
     een link role="button" en de naam "… — vergroten". */
  const vergrootbaar = useCallback(
    (el: Element) => Boolean(el.closest('.kb-prose')) && !el.closest('.kb-artikelkaart'),
    [],
  );

  /* Afbeeldingen in de artikeltekst bedienbaar maken.
     Ze komen uit dangerouslySetInnerHTML, dus er is geen JSX om props op te
     zetten; het gebeurt hier op de DOM. Zonder dit is de vergroting alleen
     met een muis te bereiken — een <img> is niet focusbaar. */
  useEffect(() => {
    const scope = scopeRef.current;
    if (!scope) return;
    const mark = () => {
      for (const el of scope.querySelectorAll<HTMLImageElement>('.kb-prose img')) {
        if (el.dataset.kbZoom || !vergrootbaar(el)) continue;
        el.dataset.kbZoom = '1';
        el.tabIndex = 0;
        el.setAttribute('role', 'button');
        el.setAttribute('aria-label', `${el.alt || 'Afbeelding'} — vergroten`);
      }
    };
    mark();
    // De inhoud wisselt bij navigatie naar een ander artikel.
    const mo = new MutationObserver(mark);
    mo.observe(scope, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, [scopeRef, vergrootbaar]);

  const open = useCallback((src: string, name: string, from: HTMLElement | null) => {
    restoreTo.current = from;
    setImg({ src, name });
  }, []);

  useEffect(() => {
    const scope = scopeRef.current;
    if (!scope) return;

    const openFrom = (target: HTMLElement): boolean => {
      const trigger = target.closest<HTMLElement>('[data-lightbox]');
      if (trigger) {
        open(trigger.dataset.lightbox || '', trigger.dataset.lightboxName || 'Afbeelding', trigger);
        return true;
      }
      if (target instanceof HTMLImageElement && vergrootbaar(target)) {
        open(target.src, target.alt || 'Afbeelding', target);
        return true;
      }
      return false;
    };

    const onClick = (e: MouseEvent) => {
      if (openFrom(e.target as HTMLElement)) e.preventDefault();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const target = e.target as HTMLElement;
      // [data-lightbox]-triggers zijn echte knoppen; die vuren zelf al een
      // click bij Enter/Space. Alleen de prose-afbeeldingen hebben dit nodig.
      if (!(target instanceof HTMLImageElement) || !vergrootbaar(target)) return;
      e.preventDefault();
      openFrom(target);
    };

    scope.addEventListener('click', onClick);
    scope.addEventListener('keydown', onKey);
    return () => {
      scope.removeEventListener('click', onClick);
      scope.removeEventListener('keydown', onKey);
    };
  }, [scopeRef, open, vergrootbaar]);

  const close = useCallback(() => setImg(null), []);

  useEffect(() => {
    if (!img) return;
    document.body.style.overflow = 'hidden';

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const items = [...dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)]
        .filter((el) => el.offsetParent !== null);
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
      // Terug naar de afbeelding waar je vandaan kwam, niet naar <body>.
      restoreTo.current?.focus?.();
    };
  }, [img, close]);

  if (!img) return null;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={img.name}
      className="kb-no-print fixed inset-0 z-300 flex flex-col bg-scrim backdrop-blur-sm motion-safe:animate-scale-in"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="surface-glass flex items-center justify-between gap-3 px-4 py-2.5">
        <span className="truncate text-base font-medium text-ink" title={img.name}>{img.name}</span>
        <div className="flex items-center gap-1">
          <a
            href={`${downloadBase}/download?url=${encodeURIComponent(img.src)}`}
            className="rounded-md p-2 text-ink-2 transition hover:bg-surface-hover hover:text-ink"
            aria-label="Afbeelding downloaden" title="Downloaden"
          >
            <Download size={17} />
          </a>
          <a
            href={img.src} target="_blank" rel="noopener noreferrer"
            className="rounded-md p-2 text-ink-2 transition hover:bg-surface-hover hover:text-ink"
            aria-label="In een nieuw tabblad openen" title="In een nieuw tabblad openen"
          >
            <ExternalLink size={17} />
          </a>
          <button
            type="button" onClick={close} autoFocus
            className="rounded-md p-2 text-ink-2 transition hover:bg-surface-hover hover:text-danger"
            aria-label="Sluiten" title="Sluiten (Esc)"
          >
            <X size={18} />
          </button>
        </div>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center p-4" onClick={close}>
        <img
          src={img.src}
          alt={img.name}
          className="max-h-full max-w-full rounded-lg object-contain shadow-xl"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
}
